import type { DecryptedNotification, NotificationAction } from "@reflecto/client-core";
import { useCallback, useMemo } from "react";
import {
  base64ImageDataUrl,
  formatRelativeTime,
  type RelativeTimeLabels,
} from "../../utils/format";
import { Notification, type NotificationAction as PrimitiveAction } from "../Notification";
import { ReplyComposer } from "../ReplyComposer";
import styles from "./styles.module.css";
import { type ActionResult, useActionStates } from "./useActionStates";
import { type ReplyResult, useReplyState } from "./useReplyState";

export type { ActionResult, ReplyResult };

/**
 * i18n bag the host supplies. Every label the headless component
 * surfaces is enumerated here so this package stays free of any
 * specific i18n stack — extension routes via `browser.i18n`, web
 * routes via its own catalog, future Electron/Tauri likewise.
 *
 * `notificationImageAlt` is shown only when the notification carries
 * an image and has no body text to fall back to. `unread` is the
 * SR-only label on the unread dot.
 */
export interface NotificationListItemLabels {
  /**
   * Text for the relative timestamp on this card. Required: it was English-only
   * inside `formatRelativeTime` until desktop slice 5 (R1-f), which is how
   * "Zuletzt verwendet just now" reached German users.
   */
  relativeTime: RelativeTimeLabels;
  replyAriaLabel: string;
  replyPlaceholder: string;
  /** ARIA label for the reply composer's send button. */
  sendLabel: string;
  sent: string;
  failedToSend: string;
  unread: string;
  dismiss: string;
  notificationImageAlt: string;
}

export interface NotificationListItemProps {
  notification: DecryptedNotification;
  /** Hide the app icon. Used by grouped views where the group header
   *  already shows it. Does NOT affect the app name — the primitive's
   *  Default variant requires `appName`. */
  showAppIcon?: boolean;
  indent?: boolean;
  /**
   * Raw base64-encoded app icon (no data-URL prefix), as stored in
   * the host's `appIconCache` keyed by package name. The component
   * runs `base64ImageDataUrl` internally to detect the mime and
   * build the `<img src>`. Host stays free of the format helper —
   * it just looks up the cache entry by `notification.appPackage`.
   */
  appIconUrl?: string;
  /**
   * Dismiss callback. Receives the notification's `msgId`. Hosts
   * route this through whatever transport applies — extension
   * batches into a debounced `DISMISS_NOTIFICATION` IPC; web posts
   * `DELETE /v1/messages/:msgId` directly.
   */
  onDismiss: (msgId: string) => void;
  /**
   * Reply submit callback — receives the trimmed reply text plus
   * the action index. Returns a Promise the internal reply FSM
   * uses to flip to "sent" / "error". When omitted (or when the
   * notification has no reply action), the ReplyComposer is hidden.
   */
  onSendReply?: (msgId: string, actionIndex: number, text: string) => Promise<ReplyResult>;
  /**
   * Button-action dispatch — receives the action index. Promise
   * result drives the per-action sending/sent/error feedback. When
   * omitted, button actions still render but their feedback states
   * stay idle (the host can choose to gray them out or treat the
   * absence as a no-op).
   */
  onDispatchAction?: (msgId: string, actionIndex: number) => Promise<ActionResult>;
  /**
   * When true, the reply composer and button actions are withheld.
   *
   * For a phone the server cannot wake — no push route registered — a reply would be accepted by
   * the UI and then simply not happen, or happen much later once the phone next opens the app.
   * A dead textarea, or a control that silently swallows what the user typed, is worse than no
   * control. See `docs/superpowers/specs/2026-08-07-android-push-fallback-design.md` D4: these
   * interactions are DISABLED on the foreground tier rather than delivered late.
   *
   * Withheld silently. An earlier cut printed a sentence in the composer's place, which appeared
   * on every card carrying any interaction and — sharing the "Sent" confirmation's success style
   * — read as a green alert repeated down the whole list. Which device can be woken is reported
   * once, on the device row and bridge card, not per notification.
   *
   * Non-interactive affordances (dismiss, opening links) are untouched — those either work
   * locally or tolerate arriving late.
   */
  interactionsUnavailable?: boolean;
  /**
   * Opens a public-API link (the payload `url` or a `linkActions[].url`)
   * when the user clicks the corresponding pill. Hosts route this to the
   * most appropriate surface — extension → `browser.tabs.create`, web →
   * `window.open`. When omitted, falls back to
   * `window.open(url, "_blank", "noopener,noreferrer")`. The URL is
   * already http(s)-validated upstream in `parsePublicSendEnvelope`.
   */
  onOpenUrl?: (url: string) => void;
  /**
   * Visual-only exit flag. When the list (`SidePanelNotificationList`)
   * intercepts a dismiss, it flips this on for ~180ms so the card
   * fades + slides out before the parent removes the data and the
   * card unmounts. While dismissing the card is also made
   * non-interactive via CSS `pointer-events: none` so a second click
   * can't fire a second dismiss.
   */
  dismissing?: boolean;
  labels: NotificationListItemLabels;
}

/** Action triage: accept an explicit `actionType: "reply"` or an
 *  absent `actionType` field (legacy wire payloads that predate the
 *  field). Any new actionType Android ships — `smartReply`,
 *  `voiceReply`, etc. — must be added here explicitly, otherwise
 *  it'll fall through to `isButtonAction` as `false` and drop out of
 *  both slots. Silent drops beat silent routing to the wrong slot. */
function isReplyAction(action: NotificationAction): boolean {
  return action.actionType === "reply" || action.actionType === undefined;
}

function isButtonAction(action: NotificationAction): boolean {
  return action.actionType === "button";
}

/** Hostname of an http(s) URL, for use as a link-pill label when the
 *  sender supplied no `url_title`. Returns "" on a malformed URL (the
 *  caller falls back to the raw URL string). */
function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

/**
 * NotificationListItem — single-notification card wrapper, owns the
 * reply + action FSMs and composes the `Notification` primitive.
 *
 * Headless: no `browser.*`, no `chrome.*`, no `theme.ts`, no storage
 * hooks. Transport (reply, action, dismiss) and i18n labels are
 * injected by the host. The two consumers (extension + web) live in
 * `apps/*` and provide thin wrappers that wire the callbacks.
 */
export function NotificationListItem({
  notification,
  showAppIcon = true,
  indent = false,
  appIconUrl,
  onDismiss,
  onSendReply,
  onDispatchAction,
  interactionsUnavailable,
  onOpenUrl,
  dismissing,
  labels,
}: NotificationListItemProps) {
  const replyAction = notification.actions?.find(isReplyAction);
  const buttonActions = useMemo(
    () => notification.actions?.filter(isButtonAction) ?? [],
    [notification.actions]
  );
  // Capture the primitive `actionIndex` for the memo deps.
  // `replyAction` is a fresh object on every storage-driven re-render
  // (notification is re-deserialised), so depending on it directly
  // busts the memo cache every cycle. The actionIndex is stable for
  // the life of the card.
  const replyActionIndex = replyAction?.actionIndex;

  const replySubmit = useMemo(
    () => async (text: string) => {
      if (!onSendReply || replyActionIndex === undefined) return { success: false };
      return onSendReply(notification.msgId, replyActionIndex, text);
    },
    [onSendReply, notification.msgId, replyActionIndex]
  );

  const reply = useReplyState({
    canReply: Boolean(replyActionIndex !== undefined && onSendReply),
    fallbackErrorLabel: labels.failedToSend,
    submit: replySubmit,
  });

  const actionDispatch = useMemo(
    () => async (actionIndex: number) => {
      if (!onDispatchAction) return { success: false };
      return onDispatchAction(notification.msgId, actionIndex);
    },
    [onDispatchAction, notification.msgId]
  );

  const { states: actionStates, dispatch: dispatchAction } = useActionStates({
    dispatch: actionDispatch,
  });

  // Depend on the destructured values, not the hook-return object
  // literal — the hook returns a fresh wrapper per render, so
  // depending on it would defeat this memo even though the two
  // fields inside are individually stable.
  const primitiveActions: PrimitiveAction[] = useMemo(
    () =>
      buttonActions.map((a) => ({
        id: String(a.actionIndex),
        label: a.label,
        onClick: () => dispatchAction(a.actionIndex),
        state: actionStates[a.actionIndex],
      })),
    [buttonActions, actionStates, dispatchAction]
  );

  // Public-API link affordances: the payload `url` (labeled by `urlTitle`,
  // falling back to its hostname) and any `linkActions`. Two-step memo mirrors
  // the buttonActions/primitiveActions pattern above — derive the stable
  // {id,label,url} data first, then build the onClick closures separately so
  // the closure layer only recomputes when `onOpenUrl` changes. (Like
  // buttonActions, the data memo still busts when the re-deserialized
  // notification yields a fresh `linkActions` array each storage flush — an
  // accepted limitation shared with the existing pattern, not a regression.)
  const linkActionData = useMemo(() => {
    const items: { id: string; label: string; url: string }[] = [];
    if (notification.url) {
      const url = notification.url;
      items.push({ id: "public-url", label: notification.urlTitle || hostnameOf(url) || url, url });
    }
    for (const [i, action] of (notification.linkActions ?? []).entries()) {
      items.push({ id: `link-${i}`, label: action.label, url: action.url });
    }
    return items;
  }, [notification.url, notification.urlTitle, notification.linkActions]);

  const linkActions: PrimitiveAction[] = useMemo(() => {
    const openUrl =
      onOpenUrl ?? ((url: string) => window.open(url, "_blank", "noopener,noreferrer"));
    return linkActionData.map((item) => ({
      id: item.id,
      label: item.label,
      onClick: () => openUrl(item.url),
    }));
  }, [linkActionData, onOpenUrl]);

  const allActions = useMemo(
    () => [...primitiveActions, ...linkActions],
    [primitiveActions, linkActions]
  );

  /** Withheld → link pills only; otherwise everything. See the `actions` prop below. */
  const visibleActions = interactionsUnavailable ? linkActions : allActions;

  const richMedia = notification.image ? (
    <img
      src={base64ImageDataUrl(notification.image)}
      alt={notification.body || labels.notificationImageAlt}
      className={styles.image}
      loading="lazy"
    />
  ) : undefined;

  const replySlot = interactionsUnavailable ? undefined : replyActionIndex !== undefined &&
    onSendReply ? (
    <>
      <ReplyComposer
        label={labels.replyAriaLabel}
        placeholder={labels.replyPlaceholder}
        sendLabel={labels.sendLabel}
        value={reply.text}
        onChange={reply.setText}
        onSend={reply.send}
        sending={reply.status === "sending"}
      />
      {reply.status === "sent" && (
        <span className={styles.replyStatus} role="status">
          {labels.sent}
        </span>
      )}
      {reply.error && (
        <span className={styles.replyError} role="status">
          {reply.error}
        </span>
      )}
    </>
  ) : undefined;

  const iconUrl = showAppIcon && appIconUrl ? base64ImageDataUrl(appIconUrl) : undefined;
  // Keyed on the primitive `notification.msgId` — the `notification`
  // object identity changes on every storage-driven re-render, but
  // its msgId string is stable for the life of the card, so the
  // handler identity stays stable across storage updates and the
  // downstream `Notification.onClose` doesn't re-render needlessly.
  const handleDismiss = useCallback(
    () => onDismiss(notification.msgId),
    [onDismiss, notification.msgId]
  );

  const commonProps = {
    appName: notification.app,
    appIconUrl: iconUrl,
    timestamp: formatRelativeTime(
      notification.timestamp || notification.receivedAt,
      labels.relativeTime
    ),
    title: notification.title,
    body: notification.body || undefined,
    bodyMarkdown: notification.markdown,
    unread: notification.isRead === false,
    unreadLabel: labels.unread,
    onClose: handleDismiss,
    closeLabel: labels.dismiss,
  };

  // Calls use `missedCall`: headered chrome + negative-tone body +
  // close, no actions/media/reply. `phoneCall` needs onAnswer/onDecline
  // the extension can't satisfy. Omitting `data-msg-id` keeps the
  // sidepanel IntersectionObserver from auto-marking calls as read;
  // the unread badge intentionally skips calls (#134) and letting the
  // observer flip isRead would create a split-brain state.
  const isCall = notification.category === "call";
  // Subtle left-edge accent for high/urgent public-API priority. Quiet
  // priorities (min/low/default) get no treatment.
  const priorityClassName =
    notification.priority === "urgent"
      ? styles.priorityUrgent
      : notification.priority === "high"
        ? styles.priorityHigh
        : undefined;
  // Combines the transient dismiss-animation class with the persistent
  // priority accent — hence the role-neutral name.
  const cardClassName =
    [dismissing ? styles.dismissing : null, priorityClassName].filter(Boolean).join(" ") ||
    undefined;
  // aria-hidden during dismiss is owned by the KbdRow wrapper, which already
  // hides this entire subtree from AT — applying it here too would just be
  // redundant noise.
  const card = isCall ? (
    <Notification variant="missedCall" {...commonProps} role="alert" className={cardClassName} />
  ) : (
    <Notification
      {...commonProps}
      richMedia={richMedia}
      // Withholding drops the phone-dependent buttons but KEEPS link pills: opening a URL is
      // local and works whether or not the phone can be woken.
      actions={visibleActions.length > 0 ? visibleActions : undefined}
      replySlot={replySlot}
      data-msg-id={notification.msgId}
      className={cardClassName}
    />
  );

  return indent ? <div className={styles.indent}>{card}</div> : card;
}
